import type { WhatsappTemplateMessageComponents } from 'types/templates';

/**
 * מערך רכיבי תבנית בפורמט Meta (למשל JSON.stringify(template.components) מהשרת הישן)
 * — מומר לאובייקט שטוח { HEADER?, BODY, FOOTER?, BUTTONS? } לתצוגה ול־getTemplateFormattedObject.
 */
export function metaComponentsArrayToFlatComponents(
  arr: unknown,
): WhatsappTemplateMessageComponents | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;

  let header: WhatsappTemplateMessageComponents['HEADER'] | undefined;
  let bodyText = '';
  let footer: WhatsappTemplateMessageComponents['FOOTER'] | undefined;
  const buttonRows: Array<{ type: 'URL' | 'QUICK_REPLY' | 'PHONE_NUMBER'; text: string; url: string | null }> =
    [];

  for (const raw of arr) {
    if (raw == null || typeof raw !== 'object') continue;
    const comp = raw as Record<string, unknown>;
    const type = String(comp.type ?? '').toUpperCase();
    const params = (Array.isArray(comp.parameters) ? comp.parameters : []) as Array<Record<string, unknown>>;

    if (type === 'BODY') {
      bodyText = joinTextParameters(params);
    } else if (type === 'HEADER') {
      header = extractHeaderBlock(comp, params);
    } else if (type === 'FOOTER') {
      footer = { text: joinTextParameters(params) || null };
    } else if (type === 'BUTTONS' && comp.buttons && Array.isArray(comp.buttons)) {
      for (const b of comp.buttons as Array<Record<string, unknown>>) {
        const bt = String(b.type ?? 'QUICK_REPLY');
        buttonRows.push({
          type: (bt === 'URL' ? 'URL' : bt === 'PHONE_NUMBER' ? 'PHONE_NUMBER' : 'QUICK_REPLY') as
            | 'URL'
            | 'QUICK_REPLY'
            | 'PHONE_NUMBER',
          text: String(b.text ?? ''),
          url: b.url != null ? String(b.url) : null,
        });
      }
    } else if (String(comp.type ?? '').toLowerCase() === 'button') {
      const p0 = params[0] as Record<string, unknown> | undefined;
      buttonRows.push({
        type: 'URL',
        text: String(p0?.text ?? ''),
        url: p0?.url != null ? String(p0.url) : null,
      });
    }
  }

  const out: WhatsappTemplateMessageComponents = {
    BODY: { text: bodyText || null },
    ...(header ? { HEADER: header } : {}),
    ...(footer ? { FOOTER: footer } : {}),
    ...(buttonRows.length > 0 ? { BUTTONS: { buttons: buttonRows } } : {}),
  };
  return out;
}

function joinTextParameters(params: Array<Record<string, unknown>>): string {
  const parts: string[] = [];
  for (const p of params) {
    if (p?.type === 'text' && typeof p.text === 'string') parts.push(p.text);
  }
  return parts.join('');
}

function extractHeaderBlock(
  comp: Record<string, unknown>,
  params: Array<Record<string, unknown>>,
): WhatsappTemplateMessageComponents['HEADER'] {
  const p0 = params[0];
  if (!p0) {
    return { format: 'TEXT', text: typeof comp.text === 'string' ? comp.text : null };
  }
  const pt = String(p0.type ?? '');
  if (pt === 'text') {
    return { format: 'TEXT', text: typeof p0.text === 'string' ? p0.text : null };
  }
  if (pt === 'image' && p0.image && typeof p0.image === 'object') {
    const link = (p0.image as { link?: string }).link;
    return { format: 'IMAGE', text: link ?? null };
  }
  if (pt === 'video' && p0.video && typeof p0.video === 'object') {
    const link = (p0.video as { link?: string }).link;
    return { format: 'VIDEO', text: link ?? null };
  }
  if (pt === 'document' && p0.document && typeof p0.document === 'object') {
    const link = (p0.document as { link?: string }).link;
    return { format: 'DOCUMENT', text: link ?? null };
  }
  return { format: 'TEXT', text: null };
}
